import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Search, X } from "lucide-react";
import { classNames } from "@/lib/format";
import { NavIcon } from "@/navigation/NavIcon";
import {
  searchStammdaten,
  type StammdatenSearchResult,
} from "@/pages/stammdaten/stammdatenSearchIndex";

const EXAMPLE_QUERIES = ["Anzahlung", "Logo", "Abreisezeit", "Preis ändern", "E-Mail"];

interface StammdatenSearchProps {
  autoFocus?: boolean;
}

/**
 * „Was möchten Sie einstellen?“ – durchsucht Bereiche und einzelne Einstellungen
 * in Alltagssprache und springt direkt zum passenden Abschnitt.
 */
export function StammdatenSearch({ autoFocus = false }: StammdatenSearchProps) {
  const navigate = useNavigate();
  const listboxId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);

  const results = useMemo<StammdatenSearchResult[]>(() => searchStammdaten(query, 8), [query]);
  const hasQuery = query.trim().length > 0;

  useEffect(() => {
    setHighlight(0);
  }, [query]);

  function go(result: StammdatenSearchResult) {
    setQuery("");
    navigate(result.href);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!hasQuery) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(results.length - 1, h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === "Enter") {
      const target = results[highlight];
      if (target) {
        e.preventDefault();
        go(target);
      }
    } else if (e.key === "Escape") {
      setQuery("");
    }
  }

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search
          size={20}
          strokeWidth={1.75}
          aria-hidden
          className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-label-tertiary"
        />
        <input
          ref={inputRef}
          type="search"
          role="combobox"
          aria-expanded={hasQuery}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-label="Einstellung suchen"
          autoFocus={autoFocus}
          autoComplete="off"
          spellCheck={false}
          className="eh-input h-12 w-full rounded-xl pl-12 pr-11 text-base shadow-sm [&::-webkit-search-cancel-button]:hidden"
          placeholder="Was möchten Sie einstellen?"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
        />
        {hasQuery ? (
          <button
            type="button"
            className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-label-tertiary hover:bg-fill hover:text-label"
            aria-label="Suche leeren"
            onClick={() => {
              setQuery("");
              inputRef.current?.focus();
            }}
          >
            <X size={16} aria-hidden />
          </button>
        ) : null}
      </div>

      {!hasQuery ? (
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-label-tertiary">
          <span>Beispiele:</span>
          {EXAMPLE_QUERIES.map((example) => (
            <button
              key={example}
              type="button"
              className="rounded-full border border-separator bg-card px-2.5 py-0.5 text-label-secondary transition hover:border-accent/40 hover:text-accent"
              onClick={() => {
                setQuery(example);
                inputRef.current?.focus();
              }}
            >
              {example}
            </button>
          ))}
        </p>
      ) : null}

      {hasQuery ? (
        <ul
          id={listboxId}
          role="listbox"
          aria-label="Suchergebnisse"
          className="overflow-hidden rounded-xl border border-separator bg-card shadow-ios"
        >
          {results.length === 0 ? (
            <li className="px-4 py-4 text-sm text-label-secondary">
              Keine Treffer für „{query.trim()}“. Versuchen Sie einen anderen Begriff oder wählen
              Sie unten einen Bereich.
            </li>
          ) : (
            results.map((result, index) => {
              const active = index === highlight;
              return (
                <li
                  key={`${result.kind}-${result.id}`}
                  role="option"
                  aria-selected={active}
                  className={classNames(
                    "border-b border-separator last:border-b-0",
                    active ? "bg-accent-soft" : "bg-card",
                  )}
                >
                  <button
                    type="button"
                    className="flex w-full items-center gap-3 px-4 py-3 text-left"
                    onMouseEnter={() => setHighlight(index)}
                    onClick={() => go(result)}
                  >
                    <span
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-fill-quaternary text-accent"
                      aria-hidden
                    >
                      {result.item.icon ? <NavIcon name={result.item.icon} size={18} /> : null}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-label">
                        {result.label}
                      </span>
                      <span className="block truncate text-xs text-label-secondary">
                        {result.hint}
                      </span>
                      <span className="mt-0.5 block truncate text-[0.6875rem] text-label-tertiary">
                        {result.group ? `${result.group.label} › ` : ""}
                        {result.kind === "setting" ? result.item.label : "Bereich öffnen"}
                      </span>
                    </span>
                    <ChevronRight size={16} aria-hidden className="shrink-0 text-label-tertiary" />
                  </button>
                </li>
              );
            })
          )}
        </ul>
      ) : null}
    </div>
  );
}
